import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  addCompanyOfficeLocation,
  type CompanyOfficeLocationPayload,
} from '@/api/v4/organization.api';
import { BaseExceptions, isBaseError } from '@/api/v4/base-fetch';
import { QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';

import { companyIdState } from '../../company.state';
import { useCompanyFetchMode } from '../../company-fetch-context';
import { useAtomValue } from 'jotai';

export const useAddOfficeLocation = () => {
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();

  const addOfficeLocationMutation = useMutation({
    mutationFn: async (payload: CompanyOfficeLocationPayload) => {
      if (!companyId) {
        return;
      }
      return addCompanyOfficeLocation(companyId, payload);
    },
    onError: (error: unknown) => {
      if (
        isBaseError(error) &&
        error.response.status === BaseExceptions.UNPROCESSABLE_ENTITY
      ) {
        return notify({ message: error.response.data.message });
      }
      return notify({ message: t`default:unknownError` });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.CompanyOfficeLocations, fetchMode, companyId],
      });
    },
  });

  const addOfficeLocation = async ({
    payload,
    onSuccess,
  }: {
    payload: CompanyOfficeLocationPayload;
    onSuccess: () => void;
  }) => {
    await addOfficeLocationMutation.mutateAsync(payload, { onSuccess });
  };

  return {
    addOfficeLocation,
  };
};
